import { type Entitlement, type EntitlementInput, resolveEntitlement } from '@parsleyai/billing'
import type { ConnectionPhase } from '@parsleyai/ui'
import { type DocumentData, type DocumentSnapshot, doc, onSnapshot } from 'firebase/firestore'
import { createContext, type ReactNode, useEffect, useReducer } from 'react'
import { db } from '@/services/firebase/config'
import { useLiveSubscription } from '@/store/userData/useLiveSubscription'
import { useUserData } from '@/store/userData/useUserData'
import { billingReducer, initialBillingState } from './billingReducer'
import { BillingActionTypes, type BillingState, type BillingSubscriptionDoc } from './types'

export interface BillingContextValue {
  state: BillingState
  /**
   * Whether this account is entitled, and to what. Recomputed on every render, deliberately never memoised —
   * the 14-day `past_due` grace expires with no Firestore write behind it, so a value memoised on
   * `[subscription]` would keep reporting `active`. `resolveEntitlement` is pure and cheap.
   */
  entitlement: Entitlement
  /** Live-connection phase for the subscription (auto-resubscribes on drop; banner-ready). */
  connection: ConnectionPhase
  /** Manual resubscribe. */
  retry: () => void
}

export const BillingContext = createContext<BillingContextValue | undefined>(undefined)

function getDocRef(uid: string) {
  return doc(db, 'users', uid, 'billing', 'subscription')
}

/**
 * Reduce the document to the plain-data shape `@parsleyai/billing` accepts. Firestore `Timestamp`s become
 * epoch millis at this boundary, which is what lets the entitlement package stay dependency-free.
 */
function toEntitlementInput(sub: BillingSubscriptionDoc | null): EntitlementInput | null {
  if (!sub) return null
  return {
    status: sub.status,
    tier: sub.tier,
    pastDueSinceMs: sub.pastDueSince ? sub.pastDueSince.toMillis() : null
  }
}

export function BillingProvider({ children }: { children: ReactNode }) {
  const { uid } = useUserData()
  const [state, dispatch] = useReducer(billingReducer, initialBillingState)

  // Drop cached billing state on sign-out; the subscription itself is disabled by the null key.
  useEffect(() => {
    if (!uid) dispatch({ type: BillingActionTypes.CLEAR })
  }, [uid])

  const { phase, retry } = useLiveSubscription<DocumentSnapshot<DocumentData>>({
    subscriptionKey: uid,
    label: 'billing',
    subscribe: (activeUid, { onData, onError }) => onSnapshot(getDocRef(activeUid), onData, onError),
    onData: (snapshot) => {
      dispatch({
        type: BillingActionTypes.SET_SUBSCRIPTION,
        payload: snapshot.exists() ? (snapshot.data() as BillingSubscriptionDoc) : null,
        nowMs: Date.now()
      })
    }
  })

  // Not useMemo — see the note on `entitlement` above. The grace window expires on the clock, not
  // on a write.
  const entitlement = resolveEntitlement(toEntitlementInput(state.subscription), Date.now())

  return <BillingContext.Provider value={{ state, entitlement, connection: phase, retry }}>{children}</BillingContext.Provider>
}
